import { useState } from 'react';
import { trpc } from '../lib/trpc.js';
import { formatDate } from '../lib/format.js';
import { clsx } from 'clsx';

type LeadStatus = 'NEW' | 'CONTACTED' | 'QUALIFIED' | 'BOOKED' | 'LOST' | 'NO_SHOW';

const STATUS_COLORS: Record<string, string> = {
  NEW: 'bg-blue-100 text-blue-800',
  CONTACTED: 'bg-yellow-100 text-yellow-800',
  QUALIFIED: 'bg-purple-100 text-purple-800',
  BOOKED: 'bg-green-100 text-green-800',
  LOST: 'bg-gray-100 text-gray-600',
  NO_SHOW: 'bg-red-100 text-red-800',
};

const STATUSES: LeadStatus[] = ['NEW', 'CONTACTED', 'QUALIFIED', 'BOOKED', 'LOST', 'NO_SHOW'];

export function LeadsPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<LeadStatus | undefined>();
  const [page, setPage] = useState(1);

  const { data, isLoading } = trpc.leads.list.useQuery({
    page,
    limit: 50,
    search: search || undefined,
    status,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl text-navy">Leads</h1>
        <div className="flex gap-2 flex-wrap">
          <input
            type="search"
            placeholder="Search name, email, phone…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy w-48"
          />
          <select
            value={status ?? ''}
            onChange={(e) => { setStatus((e.target.value as LeadStatus) || undefined); setPage(1); }}
            className="border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="animate-pulse text-gray-400">Loading leads…</div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <p className="text-xs text-gray-500 mb-3">{data?.total ?? 0} leads total</p>
            <div className="overflow-x-auto -mx-5">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.leads.map((lead) => (
                    <tr key={lead.id} className="border-b border-gray-50 hover:bg-cream-tint transition-colors">
                      <td className="py-2.5 px-3 font-medium">
                        {[lead.firstName, lead.lastName].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="py-2.5 px-3 text-gray-500 text-xs">
                        {lead.email && <div>{lead.email}</div>}
                        {lead.phone && <div>{lead.phone}</div>}
                      </td>
                      <td className="py-2.5 px-3 text-xs text-gray-500 font-mono">{lead.adName ?? '—'}</td>
                      <td className="py-2.5 px-3">
                        <span className={clsx('text-xs px-2 py-0.5 rounded font-medium', STATUS_COLORS[lead.status] ?? 'bg-gray-100 text-gray-600')}>
                          {lead.status}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-xs text-gray-400">{formatDate(lead.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {data && data.totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <button className="btn-secondary text-xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</button>
              <span className="text-sm text-gray-500 self-center">Page {page} of {data.totalPages}</span>
              <button className="btn-secondary text-xs" disabled={page >= data.totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
